import {
  BLOCKED_STATE_NAMES,
  blockedStateForZip as blockedStateCodeForZip,
} from './checkout/validation';
import { CATS, FEATURED, TESTS, EverywhereTest } from './data';

const testByName = new Map(TESTS.map((t) => [t.name, t]));
const testByCode = new Map(TESTS.map((t) => [t.code, t]));

export const EVERYWHERE_PRODUCT_SLUG_PREFIX = 'everywhere-';

export function findTest(name: string): EverywhereTest | undefined {
  return testByName.get(name);
}

export function everywhereProductSlug(test: EverywhereTest): string {
  return `${EVERYWHERE_PRODUCT_SLUG_PREFIX}${test.code}`;
}

export function findTestByProductSlug(
  productSlug: string,
): EverywhereTest | undefined {
  if (!productSlug.startsWith(EVERYWHERE_PRODUCT_SLUG_PREFIX)) {
    return undefined;
  }

  return testByCode.get(
    productSlug.slice(EVERYWHERE_PRODUCT_SLUG_PREFIX.length),
  );
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Canonical slug per test: the menu sheet's slug column. */
const slugByName = new Map<string, string>();
const nameBySlug = new Map<string, string>();
for (const t of TESTS) {
  const slug = t.slug || slugify(t.name);
  slugByName.set(t.name, slug);
  nameBySlug.set(slug, t.name);
}

export function testSlug(name: string): string {
  return slugByName.get(name) ?? slugify(name);
}

/** Canonical URL path for a test's static page. */
export function testPath(name: string): string {
  return `/everywhere/tests/${testSlug(name)}/`;
}

export function findTestBySlug(slug: string): EverywhereTest | undefined {
  const name = nameBySlug.get(slug);
  return name ? testByName.get(name) : undefined;
}

export const TESTS_INDEX_PATH = '/everywhere/tests/';

/**
 * Distinct URL pushed onto history when the confirmation screen shows, so
 * ad platforms can count conversions on a page-view/URL match.
 */
export const CONFIRMATION_PATH = '/everywhere/confirmation/';

export function displayName(test: EverywhereTest): string {
  return FEATURED[test.name]?.n ?? test.name;
}

export type PriceText = {
  lab: number;
  main: string;
  sub: string;
  short: string;
  /** Compact form for index rows and sheets ("$49") */
  indexMain: string;
  indexSub: string;
};

/** Whole-dollar catalog price. Checkout formats its own tax-inclusive totals. */
export function formatEverywherePrice(value: number): string {
  return `$${Math.round(value)}`;
}

/** Lab's fee on the checkout disclaimer — always two decimals. */
export function formatLabFee(value: number): string {
  return `$${value.toFixed(2)}`;
}

/** Store lab price plus the separate clinician-review fee. */
export function priceText(lab: number, reviewFee: number): PriceText {
  const labText = formatEverywherePrice(lab);
  const reviewFeeText = formatEverywherePrice(reviewFee);

  return {
    lab,
    main: labText,
    sub: `+ ${reviewFeeText} clinician review`,
    short: `${labText} + ${reviewFeeText}`,
    indexMain: labText,
    indexSub: `+ ${reviewFeeText} review`,
  };
}

/* ---- Lab network ---- */

export type LabNetworkBrand = 'Quest Diagnostics' | 'Labcorp';

/** The single network that runs this test, or null when both do. */
export function netBrand(test: EverywhereTest): LabNetworkBrand | null {
  if (test.net === 1) return 'Quest Diagnostics';
  if (test.net === 2) return 'Labcorp';
  return null;
}

/** State restriction inferred from a complete ZIP, matching checkout. */
export function blockedStateForZip(zip: string): string | null {
  const code = blockedStateCodeForZip(zip);
  return code ? BLOCKED_STATE_NAMES[code] : null;
}

/* ---- Detail-page fact derivations (from the design) ---- */

export function sampleIcon(sample: string): 'TestTube' | 'Drop' {
  return sample === 'Urine sample' ? 'TestTube' : 'Drop';
}

export function sampleSub(sample: string): string {
  return sample === 'Urine sample'
    ? 'No needle, at your visit.'
    : 'Quick draw, under 15 min.';
}

export type PrepIcon = 'ForkKnife' | 'Clock' | 'Pill' | 'CheckCircle';

export function prepIcon(prep: string): PrepIcon {
  if (/fast/i.test(prep)) return 'ForkKnife';
  if (/morning|urine/i.test(prep)) return 'Clock';
  if (/medication/i.test(prep)) return 'Pill';
  return 'CheckCircle';
}

/** Prep detail carried by tests that need no preparation. */
export const NO_PREP_DETAIL = 'Show up as you are.';

export type PrepFact = { value: string; sub: string };

/**
 * Prep card copy. Some tests pair a "None for most" heading with real
 * instructions, so those lead with the instructions instead.
 */
export function prepFact(test: EverywhereTest): PrepFact {
  const detail = test.prepDetail.trim();
  const noPrep = !detail || detail === NO_PREP_DETAIL;

  if (/^none\b/i.test(test.prep)) {
    return noPrep ? { value: 'None', sub: detail } : { value: detail, sub: '' };
  }

  return { value: test.prep, sub: detail };
}

/** "Why people check it" chips, from the pipe-joined data field. */
export function whyChips(test: EverywhereTest): string[] {
  return test.why.split('|').filter(Boolean);
}

/**
 * Which sex a test likely resonates with more (drives the how-it-works
 * imagery on test pages). Heuristics verbatim from the design's sexFor().
 */
export function sexSkew(test: EverywhereTest): 'm' | 'f' | null {
  const s = `${test.name} ${test.aka}`.toLowerCase();
  const cat = CATS[test.cat];
  if (/estradiol/.test(s) && /ultrasensitive/.test(s)) return 'm';
  if (
    /testosterone|dihydrotestosterone|\bdht\b|\bpsa\b|prostate|semen|sperm|androst|erectile|male/.test(
      s,
    )
  ) {
    return 'm';
  }
  if (
    /estradiol|estrogen|estriol|estrone|progesterone|prolactin|mullerian|\bamh\b|\bfsh\b|\blh\b|ovarian|pcos|pregnan|\bhcg\b|menopause|cervical|\bhpv\b|vaginal|vaginosis|ca-125|ca 125|breast|17-hydroxyprogesterone|17-ohp|female/.test(
      s,
    )
  ) {
    return 'f';
  }
  if (cat === 'Autoimmune & Inflammation') return 'f';
  if (/uric acid|gout/.test(s)) return 'm';
  return null;
}

/**
 * Same-category tests closest in price (the "Commonly checked together" cards).
 * Prices live in the store, so pass `labPriceFor` from `useEverywherePricing`.
 * Tests the store does not price sort last rather than being dropped.
 */
export function alsoTests(
  test: EverywhereTest,
  labPriceFor: (t: EverywhereTest) => number | null,
  pool: EverywhereTest[] = TESTS,
  count = 3,
): EverywhereTest[] {
  const target = labPriceFor(test);

  const distance = (t: EverywhereTest): number => {
    const price = labPriceFor(t);
    if (price === null || target === null) return Number.MAX_SAFE_INTEGER;
    return Math.abs(price - target);
  };

  return pool
    .filter((t) => t.cat === test.cat && t.name !== test.name)
    .sort((a, b) => distance(a) - distance(b))
    .slice(0, count);
}

/* ---- Landing category groups (six browse cards) ---- */

/** CATS indexes contributing to each landing group. */
export const GROUP_CATS: number[][] = [
  [0],
  [7],
  [3, 6],
  [10],
  [11, 2],
  [5, 4, 9, 8, 1],
];

/** [label, hook] per landing group. */
export const GROUP_META: Array<[string, string]> = [
  ['Allergies', 'Find your trigger'],
  ['Hormones & Thyroid', 'Is it my hormones?'],
  ['Metabolic & Heart', 'Know your numbers'],
  ['Sexual Health', 'Peace of mind, discreetly'],
  ['Energy, Blood & Nutrients', 'Why am I always tired?'],
  ['Everyday Panels & Immunity', "What's my baseline?"],
];

/** Landing card display order (per the design). */
export const GROUP_ORDER = [0, 1, 3, 4, 2, 5];

/** Phosphor icon per landing group (home cards and search suggestions). */
export const HOME_GROUP_ICONS = [
  'Flower',
  'Drop',
  'Heartbeat',
  'ShieldCheck',
  'Sparkle',
  'Infinity',
] as const;

/** Icon tint per landing group: [background, icon color]. */
export const GROUP_TINT: [string, string] = ['rgb(239,249,245)', '#28724F'];

/**
 * Primary CATS index used when jumping from a group sheet to the A–Z index.
 * Groups 4 and 5 span several CATS, so they jump to "All" instead.
 */
export const GROUP_TO_CAT = [0, 7, 3, 10, 11, 5];
export const GROUP_JUMPS_TO_ALL = [false, false, true, false, true, true];

export const GROUP_SEE_ALL_LABELS = [
  'allergy',
  'hormone & thyroid',
  'metabolic & heart',
  'sexual health',
  'vitamin & blood',
  'wellness panel',
];

export function groupCount(group: number): number {
  const cats = GROUP_CATS[group];
  return TESTS.filter((t) => cats.includes(t.cat)).length;
}

export function groupFeaturedNames(group: number): string[] {
  return Object.keys(FEATURED).filter((k) => FEATURED[k].g === group);
}

/* ---- Search ---- */

const CONCEPT_KEYWORDS: Array<[string[], string[]]> = [
  [
    [
      'fertility',
      'pregnan',
      'conceive',
      'ttc',
      'hormone',
      'period',
      'pcos',
      'menopause',
      'ovul',
    ],
    ['AMH', 'FSH', 'Estradiol', 'Prolactin', 'Total Testosterone'],
  ],
  [
    [
      'weight',
      'glp',
      'ozempic',
      'wegovy',
      'zepbound',
      'semaglutide',
      'blood sugar',
      'glucose',
      'diabet',
      'insulin',
      'metabol',
    ],
    ['Hemoglobin A1c', 'Insulin', 'Comprehensive Metabolic', 'Lipid Panel'],
  ],
  [
    ['heart', 'cholesterol', 'lipid', 'apob', 'cardio', 'blood pressure'],
    ['Lipid Panel', 'Apolipoprotein B', 'Lipoprotein(a)', 'hs-CRP'],
  ],
  [
    [
      'sexual',
      'sti',
      'std',
      'herpes',
      'hiv',
      'chlamydia',
      'gonorrhea',
      'syphilis',
      'hpv',
    ],
    ['Complete STI Panel', 'HIV Test', 'Chlamydia', 'Syphilis'],
  ],
  [
    [
      'tired',
      'fatigue',
      'energy',
      'thyroid',
      'vitamin',
      'b12',
      'iron',
      'anemia',
      'ferritin',
      'nutrient',
      'sleep',
    ],
    ['TSH', 'Thyroid', 'Vitamin D', 'Ferritin', 'Vitamin B12'],
  ],
  [
    [
      'longevity',
      'wellness',
      'annual',
      'checkup',
      'check-up',
      'full body',
      'whole body',
      'psa',
      'prostate',
      'cancer',
      'screen',
      'biological age',
    ],
    ['CBC', 'Comprehensive Metabolic', 'PSA', 'Total Testosterone'],
  ],
];

const CONCEPT_NAMES = [
  'hormones & fertility',
  'metabolic',
  'heart & cholesterol',
  'sexual health',
  'energy, thyroid & nutrients',
  'longevity & advanced panels',
];

export type ConceptMatch = { group: number; tests: string[] };

/** Fuzzy concept match: maps intent-style queries to landing groups. */
export function conceptsFor(query: string): ConceptMatch[] {
  const s = (query || '').trim().toLowerCase();
  if (s.length < 3) return [];
  const out: ConceptMatch[] = [];
  CONCEPT_KEYWORDS.forEach(([keywords, tests], i) => {
    if (keywords.some((k) => s.includes(k)) || CONCEPT_NAMES[i].includes(s)) {
      out.push({ group: i, tests });
    }
  });
  return out;
}

export const SUGGESTION_CAP = 5;

export type Suggestions = { list: EverywhereTest[]; total: number };

/** Name/aka prefix-then-substring match; list capped, total kept for "View all". */
export function suggestionsFor(query: string): Suggestions {
  const s = query.trim().toLowerCase();
  if (!s) return { list: [], total: 0 };
  const starts: EverywhereTest[] = [];
  const includes: EverywhereTest[] = [];
  for (const t of TESTS) {
    const n = t.name.toLowerCase();
    const a = t.aka.toLowerCase();
    if (n.startsWith(s) || a.startsWith(s)) starts.push(t);
    else if (n.includes(s) || a.includes(s)) includes.push(t);
  }
  return {
    list: starts.concat(includes).slice(0, SUGGESTION_CAP),
    total: starts.length + includes.length,
  };
}

/** Combined search results: name matches padded out with concept seeds. */
export function searchResults(query: string): {
  tests: EverywhereTest[];
  total: number;
  concepts: ConceptMatch[];
} {
  const concepts = conceptsFor(query);
  const { list, total } = suggestionsFor(query);
  const tests = [...list];
  for (const c of concepts) {
    for (const seed of c.tests) {
      if (tests.length >= SUGGESTION_CAP) break;
      const match = TESTS.find((t) =>
        t.name.toLowerCase().includes(seed.toLowerCase()),
      );
      if (match && !tests.includes(match)) tests.push(match);
    }
  }
  return {
    tests: tests.slice(0, SUGGESTION_CAP),
    total,
    concepts: concepts.slice(0, 2),
  };
}

/** "Most requested" strip, in display order. */
export const STRIP_NAMES = [
  'TSH',
  'Total Testosterone',
  'Complete STI Panel + Urine CT/NG/Trich',
  'Hemoglobin A1c',
  'Vitamin D, 25-Hydroxy',
  'Lipid Panel',
  'Ferritin',
  'Apolipoprotein B (ApoB)',
];

export function categoryChip(test: EverywhereTest): string {
  return CATS[test.cat];
}

/* ---- Flow receipt (grouped: due today / due at the lab) ---- */

export type ReceiptRow = { label: string; amt: string };

export type ReceiptGroup = {
  head: string;
  headAmt: string;
  sub: string;
  rows: ReceiptRow[];
  /** Green-tinted "due today" treatment */
  emphasized: boolean;
};

export type Receipt = {
  groups: ReceiptGroup[];
  dueToday: number;
  labEstimate: number;
};

export function buildReceipt(
  basket: string[],
  payPath: '' | 'ins' | 'cash',
  labPriceFor: (test: EverywhereTest) => number | null,
  reviewFee: number,
): Receipt {
  const labRows: ReceiptRow[] = [];
  let labEstimate = 0;
  for (const name of basket) {
    const test = findTest(name);
    if (!test) continue;
    const lab = labPriceFor(test);
    if (lab === null) continue;
    labRows.push({ label: name, amt: `$${lab}` });
    labEstimate += lab;
  }
  const dueToday = labRows.length ? reviewFee : 0;
  const groups: ReceiptGroup[] = [
    {
      head: 'Due today',
      headAmt: `$${dueToday}`,
      sub: 'Clinician review, paid to Everlywell. One covers your whole request.',
      rows: [],
      emphasized: true,
    },
  ];
  if (labRows.length) {
    groups.push({
      head: 'Later, at your lab visit',
      headAmt: payPath === 'ins' ? 'plan rate' : `est. $${labEstimate}`,
      sub: `Nothing is charged now. ${
        payPath === 'ins'
          ? 'The lab bills your plan after your visit; estimates below are your cash backup.'
          : 'You pay the lab directly, only after your visit.'
      }`,
      rows: labRows,
      emphasized: false,
    });
  }
  return { groups, dueToday, labEstimate };
}

/* ---- Field validation and formatting ---- */

export const isValidZip = (zip: string): boolean => /^\d{5}$/.test(zip);

export const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(email.trim());

/** Optional US phone: empty is fine, otherwise 10 digits not starting 0/1. */
export function isValidPhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 0 || (digits.length === 10 && /^[2-9]/.test(digits));
}

/** Live "(555) 555-0100" formatting as the visitor types. */
export function formatPhone(value: string): string {
  const d = value
    .replace(/\D/g, '')
    .replace(/^1(?=\d{10})/, '')
    .slice(0, 10);
  if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length) {
    return value.trim().startsWith('(') && d.length === 3 ? `(${d}) ` : d;
  }
  return '';
}
